<template>
  <div class="bg-light pb-4 wrapper-parent-goals">
    <header>
      <nav-title :color="true" :title="'Parent Goals'" custom-class="parent-goals__title" />
      <nav-subtitle :icon="'playlist_add_check'" class="mb-3" />
    </header>
    <section class="p-3 flex-wrap controls-goals">
      <b-button pill variant="warning" class="px-5" @click="openCreateGoal">
        + Create Goal
      </b-button>
      <custom-button
        class="px-4"
        router
        :to="{ name: 'PlanSelectSkill', query: { type: 'learn' } }"
      >
        Skill Visuals and Videos
      </custom-button>
    </section>
    <section class="p-3">
      <parent-goal-skills-table
        :optional-answers="getGoalsSkillList.answers"
        :skills="skills"
        @onClickFavorite="favoriteSkillController"
        @onEditGoal="handleEditGoal"
        @onDeleteGoal="handleDeleteGoal"
        @onSave="handleSaveAnswers"
      />
    </section>
    <create-goal-modal
      :answers="getPlanAnswers"
      :hasEditing="hasEditing"
      :planning-skills="getPlanSkillList.skills"
      :questions="getPlanQuestionList"
      :default-skill="defaultSkill"
      @onSkillChange="handleOnChangeSkill"
      @onCreateGoal="handleCreateGoal"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import store from '@/store';
import navTitle from '@/components/general/navTitle.vue';
import navSubtitle from '@/components/general/navSubtitle.vue';
import CreateGoalModal from '@/components/planning/CreateGoalModal.vue';
import ParentGoalSkillsTable from '@/components/goal/parent/ParentGoalSkillsTable.vue';
import CustomButton from '@/components/general/CustomButton.vue';
import mixinNotificationToast from '@/mixins/notificationToast.mixin.js';

export default {
  components: {
    navTitle,
    CreateGoalModal,
    navSubtitle,
    ParentGoalSkillsTable,
    CustomButton,
  },
  mixins: [mixinNotificationToast],
  data() {
    return {
      defaultSkill: null,
      hasEditing: false,
    };
  },
  async beforeRouteEnter(to, from, next) {
    try {
      const parentId = store.getters['user/getUserId'];
      await Promise.all([
        store.dispatch('planning/FetchPlanSkillList', parentId),
        store.dispatch('planning/FetchGoalsSkillList', parentId),
        store.dispatch('parent/getParentFavoriteSkills', parentId),
      ]);
    } catch (error) {
      throw new Error('CARE: we have a problem when  trying get data....');
    } finally {
      next();
    }
  },
  computed: {
    ...mapGetters('planning', [
      'getPlanSkillList',
      'getPlanQuestionList',
      'getPlanAnswers',
      'getGoalsSkillList',
    ]),
    ...mapGetters('user', ['getUserId']),
    ...mapGetters('parent', ['getFavoriteSkills']),
    skills() {
      return this.getGoalsSkillList.skills
        .map((skill) => ({
          ...skill,
          state: this.getFavoriteSkills.includes(skill.id),
        }))
        .sort((x, y) => {
          if (x.state === y.state) {
            return 0;
          }
          return x.state ? -1 : 1;
        });
    },
  },
  methods: {
    ...mapActions('planning', [
      'FetchPlanQuestionList',
      'FetchPlanAnswers',
      'createSkillPlan',
      'createSkillPractice',
      'deleteGoalByskillId',
    ]),
    ...mapActions('parent', ['setParentFavoriteSkills']),
    async handleOnChangeSkill({ skillId }) {
      try {
        await this.FetchPlanQuestionList(skillId);
        await this.FetchPlanAnswers({ parent: this.getUserId, skill: skillId });
      } catch (error) {
        throw new Error('[UI] Error when try get the skill questions');
      }
    },
    async handleEditGoal({ skillId }) {
      this.defaultSkill = skillId;
      this.hasEditing = true;
      await this.handleOnChangeSkill({ skillId });
      this.$bvModal.show('parent-goal-modal');
    },
    translateDataToRequestDataApi(data) {
      const answers = [];
      data.forEach((value, key) => {
        if (!Array.isArray(value)) {
          answers.push({
            question: key,
            answer: value.answer,
            additional_data: value.additional_data,
          });
          return;
        }
        const data = value.map((data) => ({
          question: key,
          answer: data.answer,
          additional_data: data.additional_data,
        }));
        answers.push(...data);
      });
      return answers;
    },
    openCreateGoal() {
      this.hasEditing = false;
      this.defaultSkill = null;
      this.$bvModal.show('parent-goal-modal');
    },
    async handleCreateGoal(data) {
      try {
        const answers = this.translateDataToRequestDataApi(data);
        await this.createSkillPlan({ parent: this.getUserId, answers });
      } catch (error) {
        console.error(error);
      }
    },
    async updateFavoriteSkills(favoritesSkills) {
      try {
        await this.setParentFavoriteSkills({
          userId: this.getUserId,
          favoriteSkill: favoritesSkills,
        });
      } catch (error) {
        throw new Error('[UI] has ocurred and error when try update the favorite');
      }
    },
    async favoriteSkillController(skillId) {
      try {
        const isAFavorite = this.getFavoriteSkills.includes(skillId);
        const favoriteSkills = isAFavorite
          ? this.getFavoriteSkills.filter((favorite) => favorite !== skillId)
          : [...this.getFavoriteSkills, skillId];
        await this.updateFavoriteSkills(favoriteSkills);
      } catch (error) {
        console.error(error);
      }
    },
    async handleSaveAnswers(answers) {
      try {
        await this.createSkillPractice({ parent: this.getUserId, answers });
        this.notificationToast({
          message: 'The answer has been saved correctly',
          variant: 'success',
          title: 'Parent Goals',
        });
      } catch (error) {
        this.notificationToast({ message: error, variant: 'danger', title: 'Parent Goals' });
        console.error(error);
      }
    },
    async handleDeleteGoal({ skillId }) {
      try {
        await this.deleteGoalByskillId({ skillId, parentId: this.getUserId });
      } catch (error) {
        this.notificationToast({
          message: error.message,
          variant: 'danger',
          title: 'Parent Goals',
        });
        console.error(error.message);
      }
    },
  },
};
</script>
<style lang="scss">
.wrapper-parent-goals {
  .controls-goals {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    @media screen and (max-width: 480px) {
      button,
      a {
        width: 100%;
      }
    }
  }

  .parent-goals__title {
    section {
      gap: 1rem;
      text-align: left !important;
    }
  }

  .skill-card {
    border: solid 5px var(--color-main);
  }
}
</style>
